import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import raf from "rc-util/es/raf";
import canUseDom from "rc-util/es/Dom/canUseDom";
import { STATUS_APPEAR, STATUS_NONE, STATUS_LEAVE, STATUS_ENTER } from '../interface';
import { animationEndName, transitionEndName } from '../util/motion'; // It's safe to use `useLayoutEffect` but the warning is annoying

var useIsomorphicLayoutEffect = canUseDom() ? useLayoutEffect : useEffect;
export default function useStatus(supportMotion, visible, getElement, _ref) {
  var _ref$motionEnter = _ref.motionEnter,
      motionEnter = _ref$motionEnter === void 0 ? true : _ref$motionEnter,
      _ref$motionAppear = _ref.motionAppear,
      motionAppear = _ref$motionAppear === void 0 ? true : _ref$motionAppear,
      _ref$motionLeave = _ref.motionLeave,
      motionLeave = _ref$motionLeave === void 0 ? true : _ref$motionLeave,
      motionDeadline = _ref.motionDeadline,
      motionLeaveImmediately = _ref.motionLeaveImmediately,
      onAppearStart = _ref.onAppearStart,
      onEnterStart = _ref.onEnterStart,
      onLeaveStart = _ref.onLeaveStart,
      onAppearActive = _ref.onAppearActive,
      onEnterActive = _ref.onEnterActive,
      onLeaveActive = _ref.onLeaveActive,
      onAppearEnd = _ref.onAppearEnd,
      onEnterEnd = _ref.onEnterEnd,
      onLeaveEnd = _ref.onLeaveEnd;

  var _useState = useState(STATUS_NONE),
      _useState2 = _slicedToArray(_useState, 2),
      status = _useState2[0],
      setStatus = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      active = _useState4[0],
      setActive = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      style = _useState6[0],
      setStyle = _useState6[1];

  var mountedRef = useRef(false);
  var nextFrameRef = useRef(null);
  var deadlineRef = useRef(null); // =========================== Dom Node ===========================

  var cacheElementRef = useRef(null);

  function getDomElement() {
    var element = getElement();
    return element || cacheElementRef.current;
  } // ========================== Next Frame ==========================


  function cancelNextFrame() {
    raf.cancel(nextFrameRef.current);
  }
  /** `useLayoutEffect` will render in the closest frame which motion may not ready */


  function nextFrame(callback) {
    var delay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
    cancelNextFrame();
    nextFrameRef.current = raf(function () {
      if (delay <= 1) {
        callback();
      } else {
        nextFrame(callback, delay - 1);
      }
    });
  } // ============================ Motion ============================


  var motionEndRef = useRef({
    status: status,
    active: active,
    onAppearEnd: onAppearEnd,
    onEnterEnd: onEnterEnd,
    onLeaveEnd: onLeaveEnd
  });
  motionEndRef.current = {
    status: status,
    active: active,
    onAppearEnd: onAppearEnd,
    onEnterEnd: onEnterEnd,
    onLeaveEnd: onLeaveEnd
  };
  var onInternalMotionEnd = React.useCallback(function (event) {
    var element = getDomElement();

    if (event && !event.deadline && event.target !== element) {
      // event exists
      // not initiated by deadline
      // transitionEnd not fired by inner elements
      return;
    }

    var canEnd;
    var cache = motionEndRef.current;

    if (cache.status === STATUS_APPEAR && cache.active) {
      var _cache$onAppearEnd;

      canEnd = (_cache$onAppearEnd = cache.onAppearEnd) === null || _cache$onAppearEnd === void 0 ? void 0 : _cache$onAppearEnd.call(cache, element, event);
    } else if (cache.status === STATUS_ENTER && cache.active) {
      var _cache$onEnterEnd;

      canEnd = (_cache$onEnterEnd = cache.onEnterEnd) === null || _cache$onEnterEnd === void 0 ? void 0 : _cache$onEnterEnd.call(cache, element, event);
    } else if (cache.status === STATUS_LEAVE && cache.active) {
      var _cache$onLeaveEnd;

      canEnd = (_cache$onLeaveEnd = cache.onLeaveEnd) === null || _cache$onLeaveEnd === void 0 ? void 0 : _cache$onLeaveEnd.call(cache, element, event);
    }

    if (canEnd !== false) {
      setStatus(STATUS_NONE);
    }
  }, []);

  function removeMotionEvents(element) {
    if (element) {
      element.removeEventListener(transitionEndName, onInternalMotionEnd);
      element.removeEventListener(animationEndName, onInternalMotionEnd);
    }
  }

  function patchMotionEvents(element) {
    if (cacheElementRef.current && cacheElementRef.current !== element) {
      removeMotionEvents(cacheElementRef.current);
    }

    if (element && element !== cacheElementRef.current) {
      element.addEventListener(transitionEndName, onInternalMotionEnd);
      element.addEventListener(animationEndName, onInternalMotionEnd); // Save as cache in case dom removed trigger by `motionDeadline`

      cacheElementRef.current = element;
    }
  } // =========================== Clean Up ===========================


  function cleanUp() {
    cancelNextFrame();
    clearTimeout(deadlineRef.current);
  } // ============================ Status ============================
  // Update with new status


  useIsomorphicLayoutEffect(function () {
    if (!supportMotion) {
      return;
    }

    var isMounted = mountedRef.current;
    mountedRef.current = true; // nextFrame(() => {

    var nextStatus;
    var nextStyle; // Appear

    if (!isMounted && visible && motionAppear) {
      nextStatus = STATUS_APPEAR;
      nextStyle = onAppearStart === null || onAppearStart === void 0 ? void 0 : onAppearStart(getDomElement(), null);
    } // Enter


    if (isMounted && visible && motionEnter) {
      nextStatus = STATUS_ENTER;
      nextStyle = onEnterStart === null || onEnterStart === void 0 ? void 0 : onEnterStart(getDomElement(), null);
    } // Leave


    if (isMounted && !visible && motionLeave || !isMounted && motionLeaveImmediately && !visible && motionLeave) {
      nextStatus = STATUS_LEAVE;
      nextStyle = onLeaveStart === null || onLeaveStart === void 0 ? void 0 : onLeaveStart(getDomElement(), null);
    } // Merge status


    if (nextStatus) {
      // Clean up when status changed
      cleanUp();
      setStatus(nextStatus);
      setActive(false);
      setStyle(nextStyle);
    } // });

  }, [visible]); // Update active status

  useIsomorphicLayoutEffect(function () {
    if (!supportMotion) {
      return;
    }

    if (status && status !== STATUS_NONE && !active) {
      // Delay one frame for motion active change
      nextFrame(function () {
        var nextStyle;
        var element = getDomElement();

        switch (status) {
          case 'appear':
            {
              nextStyle = onAppearActive === null || onAppearActive === void 0 ? void 0 : onAppearActive(element, null);
              break;
            }

          case 'enter':
            {
              nextStyle = onEnterActive === null || onEnterActive === void 0 ? void 0 : onEnterActive(element, null);
              break;
            }

          case 'leave':
            {
              nextStyle = onLeaveActive === null || onLeaveActive === void 0 ? void 0 : onLeaveActive(element, null);
              break;
            }
        } // Add event handler with element


        patchMotionEvents(element);
        setActive(true);
        setStyle(nextStyle);

        if (motionDeadline > 0) {
          deadlineRef.current = setTimeout(function () {
            onInternalMotionEnd({
              deadline: true
            });
          }, motionDeadline);
        }
      });
    }
  }, [status, active]); // Clean up status if prop set to false

  useEffect(function () {
    if (status === STATUS_APPEAR && !motionAppear || status === STATUS_ENTER && !motionEnter || status === STATUS_LEAVE && !motionLeave) {
      cancelNextFrame();
      setStatus(STATUS_NONE);
      setActive(false);
    }
  }, [status, motionAppear, motionEnter, motionLeave]); // Clean up when removed

  useEffect(function () {
    return function () {
      removeMotionEvents(cacheElementRef.current);
      cleanUp();
    };
  }, []);
  return [status, status !== STATUS_NONE ? active : false, style];
}